/**
 * Environment Configuration
 * Single source of truth for all runtime configuration
 * Reads from Vite environment variables (VITE_*)
 */

export interface EnvConfig {
  apiBaseUrl: string
  authBaseUrl: string  // Supervisor URL for auth/team operations
  chatBaseUrl: string  // Agent URL for chat/streaming
  defaultUserId?: string
  agentKey?: string
  isDevelopment: boolean
  isProduction: boolean
  devAuthEmail?: string  // Auto-login email for dev mode (VITE_DEV_AUTH_EMAIL)
}

const FALLBACK_BASE_URL = 'http://localhost:9080' // supervisor gateway in dev

// Production URL - used for all production builds
const PRODUCTION_API_URL = 'https://api.xavior.ai'

const LOOPBACK_HOSTS = new Set(['localhost', '127.0.0.1', '0.0.0.0', '::1', '[::1]'])

/**
 * Rewrite a configured backend URL to use the browser's hostname when:
 *   - we're in dev mode, AND
 *   - the URL points at a loopback host (localhost / 127.0.0.1 / 0.0.0.0), AND
 *   - the browser itself is NOT on a loopback host.
 *
 * This lets cross-LAN dev work without editing env files: when you browse
 * the dev box at http://10.10.10.100:5175 from your workstation, the bundle's
 * "http://localhost:8100" gets rewritten to "http://10.10.10.100:8100" at
 * runtime so the fetch reaches the dev box's API instead of your workstation's
 * localhost. When you're browsing the dev box from itself (hostname=localhost),
 * nothing changes. Production builds (DEV=false) bypass this entirely.
 *
 * URLs whose host is already non-loopback (e.g. an explicit staging URL) are
 * returned unchanged — the caller's intent wins.
 */
export function rewriteToBrowserHostInDev(url: string): string {
  if (!import.meta.env.DEV) return url
  if (typeof window === 'undefined') return url
  try {
    const u = new URL(url)
    const browserHost = window.location.hostname
    if (LOOPBACK_HOSTS.has(u.hostname) && !LOOPBACK_HOSTS.has(browserHost)) {
      u.hostname = browserHost
      // Strip the trailing slash URL.toString() adds when there's no path,
      // so callers that template `${base}/path` don't end up with `//path`.
      return u.toString().replace(/\/$/, '')
    }
    return url
  } catch {
    return url
  }
}

function resolveApiBaseUrl(): string {
  // Explicit env variable always wins (needed for staging builds)
  const explicit = import.meta.env.VITE_API_BASE_URL as string | undefined
  if (explicit && explicit.length > 0) {
    return rewriteToBrowserHostInDev(explicit)
  }

  // PRODUCTION BUILD: Use production URL (for mobile app and deployed web)
  if (import.meta.env.PROD) {
    return PRODUCTION_API_URL
  }

  // DEV ONLY: Check if we're in production domains (shouldn't happen, but fallback)
  if (typeof window !== 'undefined') {
    const { hostname } = window.location
    if (hostname === 'app.xavior.ai' || hostname === 'demo.xavior.ai' || hostname === 'develop.xavior.ai' || hostname.endsWith('.pages.dev')) {
      return PRODUCTION_API_URL
    }
  }

  // DEV ONLY: Fallback to local development. Auto-rewrite the loopback host
  // so the same fallback works whether the browser is on the dev box or
  // accessing it across the LAN.
  return rewriteToBrowserHostInDev(FALLBACK_BASE_URL)
}

function resolveChatBaseUrl(): string {
  // 0. VITE_AGENT_URL overrides everything (new agent service on port 8300)
  const agentUrl = import.meta.env.VITE_AGENT_URL as string | undefined
  if (agentUrl && agentUrl.length > 0) {
    return rewriteToBrowserHostInDev(agentUrl)
  }

  // 1. Check explicit env variable for chat/agent service
  const explicit = import.meta.env.VITE_CHAT_BASE_URL as string | undefined
  if (explicit && explicit.length > 0) {
    console.log('[ENV] Using explicit Chat URL:', explicit)
    return rewriteToBrowserHostInDev(explicit)
  }

  // 1b. Align with dev proxy target if provided (keeps ports in one place)
  const proxyTarget = import.meta.env.VITE_CHAT_PROXY_TARGET as string | undefined
  if (proxyTarget && proxyTarget.length > 0) {
    console.log('[ENV] Using chat proxy target:', proxyTarget)
    return rewriteToBrowserHostInDev(proxyTarget)
  }

  // 1c. Respect agent port envs if present
  const agentPort =
    (import.meta.env.VITE_AGENT_PORT as string | undefined) ||

    ((import.meta.env as any).AGENT_PORT as string | undefined)
  if (agentPort && agentPort.length > 0) {
    const url = `http://localhost:${agentPort}`
    console.log('[ENV] Using agent port for chat:', url)
    return rewriteToBrowserHostInDev(url)
  }

  // 2. In production, chat goes through same gateway as API
  const apiUrl = resolveApiBaseUrl()

  // 3. For Docker dev mode (port 9080), use gateway for chat (nginx routes /chat).
  //    Match on port suffix so the check still fires after host-rewrite (the
  //    URL might now read `http://10.10.10.100:9080`, not `localhost:9080`).
  if (/:9080(\/|$)/.test(apiUrl)) {
    console.log('[ENV] Using Docker gateway for chat:', apiUrl)
    return apiUrl
  }

  // 4. For local dev mode (port 8100 specifically), use x-agent on 8300.
  //    Same port-only match as above.
  if (/:8100(\/|$)/.test(apiUrl)) {
    const agentUrl = rewriteToBrowserHostInDev('http://localhost:8300')
    console.log('[ENV] Using x-agent for chat:', agentUrl)
    return agentUrl
  }

  // 5. Otherwise use same base as API (nginx routes /chat to agent)
  console.log('[ENV] Using API gateway for chat:', apiUrl)
  return apiUrl
}

function resolveAgentKey(): string | undefined {
  // Allow runtime override via localStorage (no backend change needed)
  if (typeof window !== 'undefined') {
    const stored = localStorage.getItem('xavior.agentKey')
    if (stored) return stored
  }

  // Fall back to env variable
  return import.meta.env.VITE_AGENT_KEY as string | undefined
}

/**
 * Application environment configuration
 * Initialized once at module load
 */
function resolveAuthBaseUrl(): string {
  // Auth endpoints go through the same gateway as API
  // nginx routes /auth/* to supervisor in both prod and Docker dev
  return resolveApiBaseUrl()
}

export const env: EnvConfig = {
  apiBaseUrl: resolveApiBaseUrl(),
  authBaseUrl: resolveAuthBaseUrl(),
  chatBaseUrl: resolveChatBaseUrl(),
  defaultUserId: import.meta.env.VITE_USER_ID as string | undefined,
  agentKey: resolveAgentKey(),
  isDevelopment: import.meta.env.DEV as boolean,
  isProduction: import.meta.env.PROD as boolean,
  devAuthEmail: import.meta.env.VITE_DEV_AUTH_EMAIL as string | undefined,
}

export function getEnvFallbackWorkspaceId(): string {
  return (import.meta.env.VITE_WORKSPACE_ID as string) || (import.meta.env.VITE_TENANT_ID as string) || 'default-workspace'
}

